<div class="simulation-overview" *ngIf="isOverviewActive">
    <h2 mat-dialog-title class="text-color-primary">Simulation</h2>
    <span class="simulation-steps__caption text-color-primary mb-2">Select the nodes for simulation use, try to select them in order, once selected, click the "Select current nodes for Simulation" button.</span>

    <div class="simulation-config-list text-color-primary">
        <mat-form-field appearance="outline" class="simulation-config-list__select">
            <mat-label>Saved configurations</mat-label>
            <mat-select [(ngModel)]="selectedSimulationConfigurationId" panelClass="simulation-config-list__panel">
                <mat-option [value]="null">New configuration</mat-option>
                @for (configuration of savedSimulationConfigurations; track configuration.simID) {
                    <mat-option [value]="configuration.simID">{{ configuration.name }}</mat-option>
                }
            </mat-select>
        </mat-form-field>

        <div class="simulation-config-list__actions">
            <button
                type="button"
                mat-stroked-button
                [disabled]="!selectedSimulationConfigurationId"
                (click)="applySelectedSimulationConfiguration()">
                Restore
            </button>
            <button
                type="button"
                mat-stroked-button
                (click)="startNewSimulationConfiguration()">
                New
            </button>
            <button
                type="button"
                mat-stroked-button
                [disabled]="!(simulationNodes.getShape().length > 0)"
                (click)="saveCurrentSimulationConfiguration()">
                Save
            </button>
            <button
                type="button"
                mat-stroked-button
                [disabled]="!selectedSimulationConfigurationId"
                (click)="copySelectedSimulationConfiguration()">
                Copy
            </button>
            <button
                type="button"
                mat-stroked-button
                [disabled]="!selectedSimulationConfigurationId"
                (click)="deleteSelectedSimulationConfiguration()">
                Delete
            </button>
            <button
                type="button"
                mat-stroked-button
                (click)="simulationConfigurationImportInput.click()">
                Import
            </button>
            <button
                type="button"
                mat-stroked-button
                [disabled]="!selectedSimulationConfigurationId"
                (click)="exportSelectedSimulationConfiguration()">
                Export
            </button>
        </div>
        <input
            #simulationConfigurationImportInput
            type="file"
            accept="application/json,.json"
            class="simulation-config-list__import-input"
            (change)="importSimulationConfiguration($event)">
    </div>

    <p class="mt-2 text-color-primary">
        CTRL+Click to select the nodes for the simulation in the correct order, then click "Use Current Nodes" button bellow.
    </p>
    
    @if (currentShapeFocus.getShape().length >1) {
        <div class="d-flex justify-content-between align-items-center gap-2 mt-2 mb-2">
            <p class="text-color-primary m-0">
                {{
                     "Currently Selected ("+currentShapeFocus.getShape().length+")"
                }}
            </p>
            <button class="select-current-nodes" mat-stroked-button (click)="selectNodesForSimulation()">Use current nodes</button>
        </div>
    }

    @if (!isLoading) {

    <mat-expansion-panel class="mt-2 mb-2" [disabled]="!(simulationNodes.getShape().length > 0)">
        <mat-expansion-panel-header>
            <mat-panel-title class="text-color-primary"> Simulation Values </mat-panel-title>
        </mat-expansion-panel-header>

        <div class="simulation-config text-color-primary">
            <mat-form-field appearance="outline" class="simulation-config__field">
                <mat-label>Iterations</mat-label>
                <input matInput type="number" min="1" [(ngModel)]="simulationIterations">
            </mat-form-field>

            <mat-form-field appearance="outline" class="simulation-config__field">
                <mat-label>Simulation period minutes</mat-label>
                <input matInput type="number" min="1" [(ngModel)]="simulationPeriodMinutes">
            </mat-form-field>

            <mat-form-field appearance="outline" class="simulation-config__field">
                <mat-label>Seed</mat-label>
                <input matInput type="number" [(ngModel)]="simulationSeed">
            </mat-form-field>

            <section class="simulation-config__section">
                <div class="simulation-config__section-header">
                    <p class="simulation-step-panel__helper">Arrival rates</p>
                    <button type="button" mat-stroked-button (click)="addArrivalRateEntry()">Add rate</button>
                </div>

                @for (entry of simulationArrivalRateEntries; track $index) {
                    <div class="simulation-config__arrival-rate">
                        <mat-form-field appearance="outline" class="simulation-config__arrival-node">
                            <mat-label>Node</mat-label>
                            <mat-select [(ngModel)]="entry.nodeId">
                                @for (node of simulationNodes.getShape(); track node.dbPropierties.__EDT_UUID__) {
                                    <mat-option [value]="node.dbPropierties.__EDT_UUID__">{{ node.name }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="simulation-config__arrival-count">
                            <mat-label>Arrivals</mat-label>
                            <input matInput type="number" min="0" step="1" [(ngModel)]="entry.arrivals">
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="simulation-config__arrival-every">
                            <mat-label>Every</mat-label>
                            <input matInput type="number" min="0" step="1" [(ngModel)]="entry.intervalValue">
                            <mat-hint>Rate: {{ getArrivalRateForEntry(entry) | number:'1.0-6' }}</mat-hint>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="simulation-config__arrival-unit">
                            <mat-label>Unit</mat-label>
                            <mat-select [(ngModel)]="entry.intervalUnit">
                                @for (unit of arrivalRateTimeUnitOptions; track unit.value) {
                                    <mat-option [value]="unit.value">{{ unit.label }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>

                        <button
                            type="button"
                            mat-icon-button
                            class="simulation-config__arrival-remove"
                            (click)="removeArrivalRateEntry($index)"
                            aria-label="Remove arrival rate">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                } @empty {
                    <p class="simulation-step-links__empty">No arrival rates.</p>
                }
            </section>

            <section class="simulation-config__section">
                <div class="simulation-config__section-header">
                    <p class="simulation-step-panel__helper">Resources structure</p>
                    <button type="button" mat-stroked-button (click)="addSimulationResource()">Add resource</button>
                </div>

                @for (resource of simulationResourcesStructure; track $index) {
                    <div class="simulation-resource">
                        <div class="simulation-resource__header">
                            <p class="simulation-resource__title">{{ resource.resource_name || ('Resource ' + ($index + 1)) }}</p>
                            <button
                                type="button"
                                mat-icon-button
                                (click)="removeSimulationResource($index)"
                                aria-label="Remove resource">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>

                        <mat-form-field appearance="outline" class="simulation-config__field">
                            <mat-label>Resource ID</mat-label>
                            <input matInput [(ngModel)]="resource.resource_id">
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="simulation-config__field">
                            <mat-label>Resource name</mat-label>
                            <input matInput [(ngModel)]="resource.resource_name">
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="simulation-config__field">
                            <mat-label>Capacity</mat-label>
                            <input
                                matInput
                                type="number"
                                min="0"
                                step="1"
                                [ngModel]="resource.capacity"
                                (ngModelChange)="setSimulationResourceCapacity(resource, $event)">
                        </mat-form-field>

                        <div class="simulation-resource__subsection">
                            <div class="simulation-resource__subsection-header">
                                <p class="simulation-step-panel__helper">Items</p>
                            </div>

                            @for (item of resource.items; track item) {
                                <div class="simulation-resource__row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Item name</mat-label>
                                        <input matInput [(ngModel)]="item.item_name">
                                    </mat-form-field>
                                </div>
                            } @empty {
                                <p class="simulation-step-links__empty">No items.</p>
                            }
                        </div>

                        <div class="simulation-resource__subsection">
                            <div class="simulation-resource__subsection-header">
                                <p class="simulation-step-panel__helper">Interruptions</p>
                                <button type="button" mat-stroked-button (click)="addSimulationResourceInterruption(resource)">Add interruption</button>
                            </div>

                            @for (interruption of resource.interruptions; track $index) {
                                <div class="simulation-resource__interruption">
                                    <div class="simulation-resource__interruption-main">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Type</mat-label>
                                            <input matInput [(ngModel)]="interruption.type">
                                        </mat-form-field>

                                        <mat-form-field appearance="outline">
                                            <mat-label>Reason</mat-label>
                                            <input matInput [(ngModel)]="interruption.reason">
                                        </mat-form-field>
                                    </div>

                                    <div class="simulation-resource__interruption-meta">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Every minutes</mat-label>
                                            <input matInput type="number" min="0" [(ngModel)]="interruption.every_minutes">
                                        </mat-form-field>

                                        <mat-form-field appearance="outline">
                                            <mat-label>Duration minutes</mat-label>
                                            <input matInput type="number" min="0" [(ngModel)]="interruption.duration_minutes">
                                        </mat-form-field>

                                        <button
                                            type="button"
                                            mat-icon-button
                                            (click)="removeSimulationResourceInterruption(resource, $index)"
                                            aria-label="Remove interruption">
                                            <mat-icon>close</mat-icon>
                                        </button>
                                    </div>
                                </div>
                            } @empty {
                                <p class="simulation-step-links__empty">No interruptions.</p>
                            }
                        </div>
                    </div>
                } @empty {
                    <p class="simulation-step-links__empty">No resources.</p>
                }
            </section>
        </div>
    </mat-expansion-panel>

    <mat-expansion-panel class="mt-2 mb-2" [disabled]="!(simulationNodes.getShape().length > 0)">
        <mat-expansion-panel-header>
            <mat-panel-title class="text-color-primary"> Simulation Schedule </mat-panel-title>
        </mat-expansion-panel-header>

        <div class="simulation-schedule text-color-primary">
            <mat-form-field appearance="outline" class="simulation-schedule__field">
                <mat-label>Simulation start</mat-label>
                <input matInput type="datetime-local" [(ngModel)]="simulationStartDatetime">
            </mat-form-field>

            <mat-form-field appearance="outline" class="simulation-schedule__field">
                <mat-label>Timezone</mat-label>
                <mat-select [(ngModel)]="simulationTimezone">
                    @for (timezone of timezones; track timezone) {
                        <mat-option [value]="timezone">{{ timezone }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>

            <section class="simulation-schedule__section">
                <p class="simulation-step-panel__helper">Workdays</p>
                <div class="simulation-schedule__workdays">
                    @for (weekday of weekdayOptions; track weekday.code) {
                        <mat-checkbox [(ngModel)]="weekday.selected">{{ weekday.label }}</mat-checkbox>
                    }
                </div>
            </section>

            <section class="simulation-schedule__section">
                <div class="simulation-schedule__section-header">
                    <p class="simulation-step-panel__helper">Shifts</p>
                    <button type="button" mat-stroked-button (click)="addSimulationShift()">Add shift</button>
                </div>

                @for (shift of simulationShifts; track $index) {
                    <div class="simulation-schedule__row">
                        <mat-form-field appearance="outline">
                            <mat-label>Start time</mat-label>
                            <input matInput type="time" [(ngModel)]="shift.start_time">
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>End time</mat-label>
                            <input matInput type="time" [(ngModel)]="shift.end_time">
                        </mat-form-field>

                        <button
                            type="button"
                            mat-icon-button
                            [disabled]="simulationShifts.length === 1"
                            (click)="removeSimulationShift($index)"
                            aria-label="Remove shift">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                }
            </section>

            <section class="simulation-schedule__section">
                <div class="simulation-schedule__section-header">
                    <p class="simulation-step-panel__helper">Holidays</p>
                    <button type="button" mat-stroked-button (click)="addSimulationHoliday()">Add holiday</button>
                </div>

                @for (holiday of simulationHolidays; track $index) {
                    <div class="simulation-schedule__holiday-row">
                        <mat-form-field appearance="outline">
                            <mat-label>Holiday</mat-label>
                            <input matInput type="date" [(ngModel)]="simulationHolidays[$index]">
                        </mat-form-field>

                        <button
                            type="button"
                            mat-icon-button
                            (click)="removeSimulationHoliday($index)"
                            aria-label="Remove holiday">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                } @empty {
                    <p class="simulation-step-links__empty">No holidays.</p>
                }
            </section>
        </div>
    </mat-expansion-panel>

    <mat-expansion-panel class="mt-2 mb-2" [disabled]="!(simulationNodes.getShape().length > 0)">
        <mat-expansion-panel-header>
            <mat-panel-title class="text-color-primary"> Global Sliders </mat-panel-title>
        </mat-expansion-panel-header>
        <div class="position-relative">
            
            <h4 class="text-color-primary">Global Sliders</h4>
            <p class="text-color-primary">Move the Sliders to simulate escenarios.</p>
            <div class="mb-3">
                <control-slider [control]="globalWorkerSlider"></control-slider>
            </div>
            <div class="mb-3">
                <control-slider [control]="globalTimeSlider"></control-slider>
            </div>
            <div class="mb-3">
                <control-slider [control]="globalWaitSlider"></control-slider>
            </div>
        </div>
    </mat-expansion-panel>

    <mat-expansion-panel [disabled]="!(simulationNodes.getShape().length > 0)">
        <mat-expansion-panel-header>
            <mat-panel-title class="text-color-primary"> Simulation Steps </mat-panel-title>
            <mat-panel-description> Nodes Selected: {{simulationNodes.getShape().length}} </mat-panel-description>
        </mat-expansion-panel-header>

        <section class="simulation-steps">
            <div class="simulation-steps__header text-color-primary">
                <h4 class="mb-0">Simulation Steps</h4>
                <span class="simulation-steps__caption">Expand any step to inspect it, and drag only from the handle to
                    change the order.</span>
                <p>
                    {{
                    "Selected Nodes for Simulation ("+simulationNodes.getShape().length+")"
                    }}
                </p>
                <button
                    type="button"
                    mat-stroked-button
                    class="simulation-steps__calculate-order"
                    (click)="recalculateSimulationStepIds()">
                    Recalcular IDs
                </button>
            </div>

            <div cdkDropList class="simulation-steps__list" [cdkDropListData]="simulationNodes.getShape()"
                (cdkDropListDropped)="reorderSimulationSteps($event)">
                <button
                    type="button"
                    mat-stroked-button
                    class="simulation-steps__add"
                    (click)="createSimulationStep()">
                    <mat-icon>add</mat-icon>
                    New step
                </button>
                @for (step of simulationNodes.getShape(); track step.dbPropierties.__EDT_UUID__; let index = $index) {
                    <div class="simulation-step-item" cdkDrag cdkDragLockAxis="y">
                        <app-simulation-step-panel
                            [step]="step"
                            [stepOrder]="getSimulationStepOrder(step, index)"
                            [title]="'Step ' + (index + 1)"
                            [description]="step.name || ('Node ' + (index + 1))"
                            [relation]="stepRelations[step.dbPropierties.__EDT_UUID__]"
                            [simulationSteps]="simulationNodes.getShape()"
                            [stepCodeByNodeId]="stepCodeByNodeId"
                            [simulationResourcesStructure]="simulationResourcesStructure"
                            [taskDurationVariabilityOptions]="taskDurationVariabilityOptions"
                            [previusTaskDepencency]="previusTaskDepencency"
                            [nextTaskType]="nextTaskType"
                            [showDragHandle]="true">
                        </app-simulation-step-panel>
                    </div>
                }
            </div>
        </section>
    </mat-expansion-panel>

    <mat-expansion-panel class="mt-2 mb-2" [disabled]="!(simulationNodes.getShape().length > 0) || !(currentShapeFocus.getShape().length > 0)">
        <mat-expansion-panel-header>
            <mat-panel-title class="text-color-primary"> Selected Nodes Sliders </mat-panel-title>
            <mat-panel-description> Nodes Selected: {{currentShapeFocus.getShape().length}} </mat-panel-description>
        </mat-expansion-panel-header>
        <div class="position-relative">
            @if (currentShapeFocus.getShape().length) {
                <h4 class="text-color-primary">Selected Sliders</h4>
                <p class="text-color-primary">
                    {{ 
                        (currentShapeFocus.getShape().length == 1) ? 
                        currentShapeFocus.getShape()[0].name : "Currently Selected ("+currentShapeFocus.getShape().length+")" 
                    }}
                </p>
                <div class="mb-3">
                    <control-slider [control]="workersSlider"></control-slider>
                </div>
                <div class="mb-3">
                    <control-slider [control]="timeSlider"></control-slider>
                </div>
                <div class="mb-3">
                    <control-slider [control]="waitSlider"></control-slider>
                </div>

                <div class="selected-node-values">
                    @for (node of currentShapeFocus.getShape(); track node.dbPropierties.__EDT_UUID__) {
                        @if (node.simulationValues && stepRelations[node.dbPropierties.__EDT_UUID__]) {
                            <app-simulation-step-panel
                                [step]="node"
                                [stepOrder]="getSimulationStepCodeByNodeId(node.dbPropierties.__EDT_UUID__)"
                                [title]="'' + (getSimulationStepCodeByNodeId(node.dbPropierties.__EDT_UUID__) || 'Node')"
                                [description]="node.name"
                                [relation]="stepRelations[node.dbPropierties.__EDT_UUID__]"
                                [simulationSteps]="simulationNodes.getShape()"
                                [stepCodeByNodeId]="stepCodeByNodeId"
                                [simulationResourcesStructure]="simulationResourcesStructure"
                                [taskDurationVariabilityOptions]="taskDurationVariabilityOptions"
                                [previusTaskDepencency]="previusTaskDepencency"
                                [nextTaskType]="nextTaskType">
                            </app-simulation-step-panel>
                        } @else {
                            <mat-expansion-panel class="simulation-step-panel">
                                <mat-expansion-panel-header class="simulation-step-panel__header">
                                    <mat-panel-title class="text-color-primary">
                                        {{ getSimulationStepCodeByNodeId(node.dbPropierties.__EDT_UUID__) || "Node" }}
                                    </mat-panel-title>

                                    <mat-panel-description class="card-node-name">
                                        {{ node.name }}
                                    </mat-panel-description>
                                </mat-expansion-panel-header>

                                <p class="simulation-step-links__empty selected-node-values__empty">
                                    This node is not part of the current simulation selection.
                                </p>
                            </mat-expansion-panel>
                        }
                    }
                </div>
            }
        </div>
    </mat-expansion-panel>

    <div class="d-flex justify-content-center align-items-center flex-column gap-2 mt-3">
        <button class="mt-2" mat-raised-button (click)="sendSimulation()">SIMULATE</button>

        @if (simulationOutputFiles.length) {
            <div class="mt-2 simulation-output-files">
                @for (file of simulationOutputFiles; track file.name) {
                    <button
                        mat-stroked-button
                        [disabled]="!file.path"
                        (click)="downloadSimulationOutputFile(file)">
                        {{ getSimulationOutputFileLabel(file) }}
                    </button>
                }
            </div>
        }
        
        <div class="mt-3 simulation-config-list__actions">
            <button mat-stroked-button [disabled]="!simulationFullResult" (click)="exportSimulationResult()">EXPORT RESULT JSON</button>
            <button mat-stroked-button (click)="simulationResultFileInput.click()">LOAD RESULT JSON</button>
            <input
                #simulationResultFileInput
                type="file"
                accept="application/json,.json"
                class="d-none"
                (change)="importSimulationResult($event)">
        </div>
    </div>

    }@else {
        <div class="d-flex justify-content-center align-items-center">
            <mat-progress-spinner class="bright-spinner" mode="indeterminate"></mat-progress-spinner>
        </div>
    }
</div>
